import React from "react"
import {
  createStyles,
  forwardRef,
  HTMLStyledProps,
} from "@design-system/styled-system"
import { Text } from "./Text"
import { TextProps } from "./types"

export interface HeadingProps extends TextProps {
  level: 1 | 2 | 3 | 4 | 5 | 6
}

export const Heading = forwardRef<HeadingProps, "h2">((props, ref) => {
  const { level, style, ...rest } = props

  return (
    <Text
      role="heading"
      aria-level={level}
      {...rest}
      ref={ref}
      style={[styles.heading, level && styles[`level${level}`], style]}
    />
  )
})

Heading.displayName = "Heading"

Heading.defaultProps = {
  level: 2,
}

const styles = createStyles((theme) => ({
  heading: {
    display: "block",
    fontWeight: theme.fontWeights.bold,
  },
  level1: {
    fontSize: theme.headingFontSizes.level1,
    lineHeight: theme.headingLineHeights.level1,
  },
  level2: {
    fontSize: theme.headingFontSizes.level2,
    lineHeight: theme.headingLineHeights.level2,
  },
  level3: {
    fontSize: theme.headingFontSizes.level3,
    lineHeight: theme.headingLineHeights.level3,
  },
  level4: {
    fontSize: theme.headingFontSizes.level4,
    lineHeight: theme.headingLineHeights.level4,
  },
  level5: {
    fontSize: theme.headingFontSizes.level5,
    lineHeight: theme.headingLineHeights.level5,
  },
  level6: {
    fontSize: theme.headingFontSizes.level6,
    lineHeight: theme.headingLineHeights.level6,
  },
}))

export type LeveledHeadingProps = Omit<HeadingProps, "level">

export const H1 = forwardRef<LeveledHeadingProps, "h1">((props, ref) => (
  <Heading {...props} level={1} ref={ref} />
))
H1.displayName = "H1"

export const H2 = forwardRef<LeveledHeadingProps, "h2">((props, ref) => (
  <Heading {...props} level={2} ref={ref} />
))
H2.displayName = "H2"

export const H3 = forwardRef<LeveledHeadingProps, "h3">((props, ref) => (
  <Heading {...props} level={3} ref={ref} />
))
H3.displayName = "H3"

export const H4 = forwardRef<LeveledHeadingProps, "h4">((props, ref) => (
  <Heading {...props} level={4} ref={ref} />
))
H4.displayName = "H4"

export const H5 = forwardRef<LeveledHeadingProps, "h5">((props, ref) => (
  <Heading {...props} level={5} ref={ref} />
))
H5.displayName = "H5"

export const H6 = forwardRef<LeveledHeadingProps, "h6">((props, ref) => (
  <Heading {...props} level={6} ref={ref} />
))
H6.displayName = "H6"
